/**
 * Sound and browser notifications for new chat messages.
 *
 * Always on, on computers: the Settings switch for it was removed on
 * 30 October 2026 (owner's call). Phones and tablets get push instead. The
 * pop-up shows only where the browser already allows notifications; nothing
 * here asks for that permission.
 *
 * These only work while a tab of the site is open. Push notifications
 * (lib/push.ts) reach the device when it is closed too; when this device has
 * them, the pop-up here is skipped and only the chime plays.
 */

import { isMobileDevice, isPushActiveHere } from '@/lib/push'

/** The conversation on screen right now, so its own messages do not notify. */
let activeThread: string | null = null

export function setActiveChatThread(id: string | null) {
  activeThread = id
}

let audio: AudioContext | null = null

/** A soft two-note chime, generated rather than shipped as a file. */
export function playChime() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audio ??= new Ctx()
    const now = audio.currentTime
    for (const [i, freq] of [880, 1320].entries()) {
      const osc = audio.createOscillator()
      const gain = audio.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t = now + i * 0.12
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.12, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35)
      osc.connect(gain).connect(audio.destination)
      osc.start(t)
      osc.stop(t + 0.4)
    }
  } catch {
    // Audio is a nicety; a browser that blocks it before any click is normal.
  }
}

/**
 * Notify about a new message, unless it is in the conversation already on
 * screen while the tab is visible. `force` is for @mentions, which notify
 * even in an open thread when the tab is in the background.
 */
export function notifyNewMessage({
  threadId,
  title,
  body,
  onClick,
}: {
  threadId: string | null
  title: string
  body: string
  onClick?: () => void
}) {
  if (isMobileDevice()) return
  const visible = document.visibilityState === 'visible'
  if (visible && threadId && threadId === activeThread) return

  playChime()
  if (visible || typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  // The push for this message is on its way and would be a second pop-up.
  if (isPushActiveHere()) return
  try {
    const n = new Notification(title, {
      body: body.length > 140 ? `${body.slice(0, 139)}…` : body,
      icon: '/favicon.ico',
      tag: threadId ?? undefined,
    })
    n.onclick = () => {
      window.focus()
      onClick?.()
      n.close()
    }
  } catch {
    // Some mobile browsers only allow notifications from a service worker.
  }
}
